import React, { Component, CSSProperties } from 'react';
import InfoIcon from '../InfoIcon';
import uniqueId from 'lodash/uniqueId';
import Text, { SIZES, SKINS, WEIGHTS } from '../Text';
import { ALIGN, DATA_HOOKS, PLACEMENT, STATUS } from './FormField.constants';
import { st, classes } from './FormField.st.css';
import {
  StatusAlertFilledSmall,
  StatusWarningFilledSmall,
} from '@wix/wix-ui-icons-common';
import { Asterisk } from '@wix/wix-ui-icons-common/system';
import { StatusContext } from './StatusContext';
import { FieldSetContext } from '../FieldSet/FieldSet.context';
import {
  FormFieldProps,
  LabelPlacement,
  LabelSize,
  StatusType,
} from './FormField.types';
import { IconThemeContext } from '../WixDesignSystemIconThemeProvider/IconThemeContext';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';

export class FormField extends Component<FormFieldProps> {
  static displayName = 'FormField';

  static contextType = WixDesignSystemContext;

  labelId: string;
  statusId: string;

  constructor(props: FormFieldProps) {
    super(props);
    this.labelId = props.labelId || uniqueId('formfield-');
    this.statusId = props.labelId
      ? `${props.labelId}-status`
      : uniqueId('formfield-status-');
  }
  static defaultProps: FormFieldProps = {
    required: false,
    stretchContent: true,
    labelSize: 'small',
    labelPlacement: PLACEMENT.top,
    labelAlignment: ALIGN.middle,
  };

  state: { lengthLeft: number | undefined } = {
    lengthLeft: undefined,
  };

  childrenRenderPropInterface = {
    setCharactersLeft: (lengthLeft: number) => this.setState({ lengthLeft }),
  };

  _getLabelPlacement = (): LabelPlacement | undefined => {
    const { mobile } = this.context as React.ContextType<
      typeof WixDesignSystemContext
    >;

    return mobile ? PLACEMENT.top : this.props.labelPlacement;
  };

  _renderChildren() {
    const { children } = this.props;
    if (typeof children === 'function') {
      return children(this.childrenRenderPropInterface);
    }

    return children;
  }

  _hasCharCounter = () =>
    this.props.charCount !== undefined ||
    typeof this.state.lengthLeft === 'number';

  charactersLeft = (lengthLeft: number | undefined) => {
    const { labelSize } = this.props;
    const colorProps =
      lengthLeft && lengthLeft >= 0
        ? { light: true, secondary: true }
        : { skin: SKINS.error };

    return (
      <Text
        className={st(classes.charCount, { labelSize })}
        size={SIZES.small}
        weight={WEIGHTS.normal}
        {...colorProps}
        dataHook={DATA_HOOKS.counter}
        children={lengthLeft}
      />
    );
  };

  _renderCharCounter = () => {
    if (!this._hasCharCounter()) {
      return;
    }
    const { charCount } = this.props;
    return this.charactersLeft(
      charCount !== undefined ? charCount : this.state.lengthLeft,
    );
  };

  _renderInfoIcon = () => {
    const { infoContent, infoTooltipProps, showInfoIconOnHover } = this.props;
    return (
      infoContent && (
        <InfoIcon
          dataHook={DATA_HOOKS.infoIcon}
          className={st(classes.infoIcon, {
            showInfoIconOnHover: String(showInfoIconOnHover),
          })}
          content={infoContent}
          tooltipProps={infoTooltipProps}
          size="small"
        />
      )
    );
  };

  _renderSuffix = () => {
    const { suffix } = this.props;

    return (
      (suffix || this._hasCharCounter()) && (
        <div
          data-hook={DATA_HOOKS.suffix}
          className={st(classes.suffix, {
            noLabel:
              !this.props.label || this._getLabelPlacement() !== PLACEMENT.top,
          })}
        >
          {suffix ? suffix : this._renderCharCounter()}
        </div>
      )
    );
  };

  _hasInlineElements = (
    label: FormFieldProps['label'],
    labelPlacement?: LabelPlacement,
  ) => {
    const hasInlineLabel =
      label &&
      (labelPlacement === PLACEMENT.left || labelPlacement === PLACEMENT.right);
    const hasInlineIndicator =
      !label && (this.props.required || !!this.props.infoContent);
    return hasInlineLabel || hasInlineIndicator;
  };

  _renderLabel = ({
    trimLongText,
    labelSize,
    asterisk,
  }: {
    trimLongText: boolean;
    labelSize?: LabelSize;
    asterisk: JSX.Element;
  }) => {
    const { label, id, required, infoContent, ellipsis, maxLines } = this.props;
    const weight = labelSize === 'tiny' ? 'normal' : undefined;
    const hasSuffix = required || infoContent;

    return (
      <Text
        size={labelSize}
        weight={weight}
        htmlFor={id}
        id={this.labelId}
        tagName="label"
        dataHook={DATA_HOOKS.label}
        ellipsis={ellipsis ?? trimLongText}
        maxLines={maxLines}
        style={{
          display: 'block', // allows the label to be centered vertically
        }}
        secondary
        className={st(classes.label, {
          labelSize,
          size: labelSize,
        })}
        suffix={
          hasSuffix ? (
            <>
              {required && asterisk}
              {this._renderInfoIcon()}
            </>
          ) : undefined
        }
      >
        {label}
      </Text>
    );
  };

  _renderStatusIcon = (icons: {
    StatusAlertFilledSmall: React.ComponentType;
    StatusWarningFilledSmall: React.ComponentType;
  }) => {
    const { status } = this.props;
    if (!status || status === 'loading') return;

    const iconByStatus: Record<
      Exclude<StatusType, 'loading'>,
      React.ComponentType
    > = {
      [STATUS.error]: icons.StatusAlertFilledSmall,
      [STATUS.warning]: icons.StatusWarningFilledSmall,
    };
    const Icon = iconByStatus[status];

    return (
      <div className={classes.statusIcon}>
        <Icon />
      </div>
    );
  };

  _renderStatusMessage = (icons: {
    StatusAlertFilledSmall: React.ComponentType;
    StatusWarningFilledSmall: React.ComponentType;
  }) => {
    const { statusMessage, status, labelSize } = this.props;

    return (
      <div
        className={st(classes.statusMessage, {
          status,
          labelSize,
        })}
      >
        {(status === STATUS.error || status === STATUS.warning) &&
          this._renderStatusIcon(icons)}
        <Text
          id={this.statusId}
          dataHook={DATA_HOOKS.statusMessage}
          skin={status === STATUS.error ? 'error' : 'standard'}
          secondary={status !== STATUS.error}
          size="tiny"
        >
          {statusMessage}
        </Text>
      </div>
    );
  };

  _getCustomColumns = (): CSSProperties['gridTemplateColumns'] => {
    const { labelWidth, inputWidth } = this.props;
    const labelPlacement = this._getLabelPlacement();

    const labelColumn = labelWidth ?? 'auto';
    const inputColumn = inputWidth ?? 'auto';

    if (labelPlacement === PLACEMENT.left) {
      return `${labelColumn} ${inputColumn}`;
    } else if (labelPlacement === PLACEMENT.right) {
      return `${inputColumn} ${labelColumn}`;
    } else return undefined;
  };

  render() {
    const {
      label,
      labelAlignment,
      labelSize,
      required,
      dataHook,
      children,
      classNames,
      stretchContent,
      statusMessage,
      status,
      labelWidth,
      inputWidth,
      ellipsis,
    } = this.props;

    const labelPlacement = this._getLabelPlacement();

    const rootStyles = label
      ? {
          labelPlacement,
          labelAlignment,
          stretchContent,
          required,
          minLabelHeight: !children,
        }
      : {
          stretchContent,
          required,
          minLabelHeight: !children,
        };
    const hasInlineElements = this._hasInlineElements(label, labelPlacement);
    const hasCustomWidth = !!labelWidth || !!inputWidth;

    return (
      <IconThemeContext.Consumer>
        {({ icons: themeIcons = {} }) => {
          const icons = {
            Asterisk: themeIcons.FormField?.Asterisk || Asterisk,
            StatusAlertFilledSmall:
              themeIcons.FormField?.StatusAlertFilledSmall ||
              StatusAlertFilledSmall,
            StatusWarningFilledSmall:
              themeIcons.FormField?.StatusWarningFilledSmall ||
              StatusWarningFilledSmall,
          };

          const asterisk = (
            <div data-hook={DATA_HOOKS.asterisk} className={classes.asterisk}>
              <icons.Asterisk />
            </div>
          );

          return (
            <div
              data-hoverable-action-trigger={true}
              data-hook={dataHook}
              data-status={status}
              className={st(
                classes.root,
                {
                  ...rootStyles,
                  ...(label ? { labelSize } : {}),
                  hasInlineElements,
                },
                classNames,
              )}
              style={
                labelPlacement !== PLACEMENT.top && hasCustomWidth
                  ? { gridTemplateColumns: this._getCustomColumns() }
                  : {}
              }
            >
              {label && labelPlacement === PLACEMENT.top && (
                <div className={classes.labelRow}>
                  <div className={classes.labelRowMain}>
                    {this._renderLabel({
                      trimLongText: true,
                      labelSize,
                      asterisk,
                    })}
                  </div>
                  {this._renderSuffix()}
                </div>
              )}

              {children && (
                <div
                  data-hook={DATA_HOOKS.children}
                  className={st(classes.children, {
                    childrenWithInlineLabel: hasInlineElements,
                  })}
                >
                  {!label && this._renderSuffix()}
                  <FieldSetContext.Consumer>
                    {fieldSet => (
                      <StatusContext.Provider
                        value={{
                          status,
                          ariaLabelledBy: this.labelId,
                          ariaDescribedBy: [
                            this.statusId,
                            status === fieldSet.status && fieldSet.statusId,
                          ]
                            .filter(Boolean)
                            .join(' '),
                        }}
                      >
                        {this._renderChildren()}
                      </StatusContext.Provider>
                    )}
                  </FieldSetContext.Consumer>
                </div>
              )}

              {hasInlineElements && (
                <div
                  className={st(classes.labelRow, {
                    ellipsis,
                  })}
                >
                  <div className={classes.labelRowMain}>
                    {this._renderLabel({
                      trimLongText: false,
                      labelSize,
                      asterisk,
                    })}
                  </div>
                  {!!label && this._renderSuffix()}
                </div>
              )}

              {statusMessage && this._renderStatusMessage(icons)}
            </div>
          );
        }}
      </IconThemeContext.Consumer>
    );
  }
}

export default FormField;
